import type {
  ClientOrderByArgs,
  ClientPaginatedResponse,
  ClientPaginationState,
} from "api-types/common/commonResponses";
import type { ClientCustomer } from "api-types/customers/customersResponses";
import type { ClientHomePlan, ClientHomePlanHydratedNoDoc } from "api-types/homePlans/homePlanResponses";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import type { FetchErrorResponse } from "common/utils/fetchHelpers";
import { getBuilderLocationMultiUnitHomePlansURL, getCustomerURL } from "lib/endpointHelpers";
import {
  keepPreviousData,
  queryOptions,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "lib/query";
import { authGet } from "lib/query/hooks/useAuthQuery";
import { customerKeys, homePlanKeys } from "lib/query/keys";
import {
  deleteHomePlan as deleteHomePlanRequest,
  postHomePlan as postHomePlanRequest,
  putHomePlan as putHomePlanRequest,
  type PostHomePlanFields,
  type PutHomePlanFields,
} from "lib/storage/homePlanStorage";
import { useEffect, useState } from "react";
import useDebounce from "ui/hooks/useDebounce";

const HOME_PLAN_PAGE_SIZE = 50;
const HOME_PLAN_SEARCH_DEBOUNCE_MS = 500;

export function getPaginatedHomePlansURL(
  fetchKey: string,
  pagination: ClientPaginationState,
  search?: string,
  orderBy?: Partial<ClientOrderByArgs<ClientHomePlanHydratedNoDoc>>,
): string {
  const [basePath, existingParams] = fetchKey.split("?");
  const params = new URLSearchParams(existingParams);
  params.set("limit", pagination.pageSize.toString());
  params.set("offset", (pagination.pageIndex * pagination.pageSize).toString());

  if (search) {
    params.set("search", search);
  } else {
    params.delete("search");
  }

  const orderByEntry = orderBy ? Object.entries(orderBy)[0] : undefined;
  if (orderByEntry) {
    params.set("orderBy", orderByEntry[0]);
    params.set("order", orderByEntry[1]);
  } else {
    params.delete("orderBy");
    params.delete("order");
  }

  return `${basePath}?${params.toString()}`;
}

export const locationHomePlansQueryOptions = (builderId: string, requestUrl: string) =>
  queryOptions({
    queryKey: [...homePlanKeys.all(builderId), "location-list", requestUrl] as const,
    queryFn: () => authGet<ClientPaginatedResponse<ClientHomePlanHydratedNoDoc>>(requestUrl),
    placeholderData: keepPreviousData,
  });

export const multiUnitHomePlansQueryOptions = (builderId: string, locationId: string) =>
  queryOptions({
    queryKey: [...homePlanKeys.all(builderId), "multi-unit-list", locationId] as const,
    queryFn: () =>
      authGet<ClientHomePlanHydratedNoDoc[]>(getBuilderLocationMultiUnitHomePlansURL({ builderId, locationId })),
  });

export const customerQueryOptions = (builderId: string, customerId: string) =>
  queryOptions({
    queryKey: [...customerKeys.all(builderId), customerId] as const,
    queryFn: () => authGet<ClientCustomer>(getCustomerURL(builderId, customerId)),
  });

export function usePaginatedHomePlans({ builderId, fetchKey }: { builderId: string; fetchKey: string }) {
  const [search, setSearch] = useState("");
  const [orderBy, setOrderBy] = useState<ClientOrderByArgs<ClientHomePlanHydratedNoDoc>>();
  const [pagination, setPagination] = useState<ClientPaginationState>({
    pageSize: HOME_PLAN_PAGE_SIZE,
    pageIndex: 0,
    pageInfoByIndex: {},
  });
  const debouncedSearch = useDebounce(search, HOME_PLAN_SEARCH_DEBOUNCE_MS);
  const requestUrl = getPaginatedHomePlansURL(fetchKey, pagination, debouncedSearch, orderBy);
  const options = locationHomePlansQueryOptions(builderId, requestUrl);
  const query = useQuery(options);

  useEffect(() => {
    setPagination((previous) => ({ ...previous, pageIndex: 0, pageInfoByIndex: {} }));
  }, [search, fetchKey]);

  return {
    ...query,
    loading: query.isLoading,
    orderBy,
    pagination,
    queryKey: options.queryKey,
    search,
    setOrderBy,
    setPagination,
    setSearch,
  };
}

export function useMultiUnitHomePlans({
  builderId,
  locationId,
  enabled,
}: {
  builderId: string;
  locationId: string;
  enabled: boolean;
}) {
  const options = multiUnitHomePlansQueryOptions(builderId, locationId);
  const query = useQuery({ ...options, queryFn: enabled ? options.queryFn : skipToken });
  return { ...query, loading: query.isLoading, queryKey: options.queryKey };
}

export function useCustomer({ builderId, customerId }: { builderId?: string; customerId?: string | null }) {
  const options = customerQueryOptions(builderId ?? "", customerId ?? "");
  const query = useQuery({ ...options, queryFn: builderId && customerId ? options.queryFn : skipToken });
  return { ...query, loading: query.isLoading };
}

function invalidateHomePlanQueries(queryClient: QueryClient, builderId: string): void {
  void queryClient.invalidateQueries({ queryKey: homePlanKeys.all(builderId) });
  void queryClient.invalidateQueries({ queryKey: customerKeys.all(builderId) });
}

async function updateHomePlan({
  builderId,
  homePlanId,
  fields,
}: {
  builderId: string;
  homePlanId: string;
  fields: PutHomePlanFields;
}): Promise<ClientHomePlan> {
  const [homePlan, error] = await putHomePlanRequest(builderId, homePlanId, fields);
  if (error) {
    throw error;
  }
  assertIsNotUndefined(homePlan);
  return homePlan;
}

async function createHomePlan({
  builderId,
  fields,
}: {
  builderId: string;
  fields: PostHomePlanFields;
}): Promise<ClientHomePlan> {
  const [homePlan, error] = await postHomePlanRequest(builderId, fields);
  if (error) {
    throw error;
  }
  assertIsNotUndefined(homePlan);
  return homePlan;
}

async function deleteHomePlan({ builderId, homePlanId }: { builderId: string; homePlanId: string }): Promise<string> {
  const [deletedHomePlanId, error] = await deleteHomePlanRequest(builderId, homePlanId);
  if (error) {
    throw error;
  }
  assertIsNotUndefined(deletedHomePlanId);
  return deletedHomePlanId;
}

interface HomePlanUpdate {
  homePlanId: string;
  fields: PutHomePlanFields;
}

interface BulkHomePlanMutationResult {
  successfulHomePlanIds: string[];
  failures: Array<{ homePlanId: string; error: FetchErrorResponse }>;
}

export function useUpdateHomePlan(builderId: string) {
  const queryClient = useQueryClient();
  return useMutation<ClientHomePlan, FetchErrorResponse, HomePlanUpdate>({
    mutationKey: [...homePlanKeys.all(builderId), "update"],
    mutationFn: ({ homePlanId, fields }) => updateHomePlan({ builderId, homePlanId, fields }),
    onSuccess: () => invalidateHomePlanQueries(queryClient, builderId),
  });
}

export function useCreateHomePlan(builderId: string) {
  const queryClient = useQueryClient();
  return useMutation<ClientHomePlan, FetchErrorResponse, PostHomePlanFields>({
    mutationKey: [...homePlanKeys.all(builderId), "create"],
    mutationFn: (fields) => createHomePlan({ builderId, fields }),
    onSuccess: () => invalidateHomePlanQueries(queryClient, builderId),
  });
}

export function useDeleteHomePlan(builderId: string) {
  const queryClient = useQueryClient();
  return useMutation<string, FetchErrorResponse, string>({
    mutationKey: [...homePlanKeys.all(builderId), "delete"],
    mutationFn: (homePlanId) => deleteHomePlan({ builderId, homePlanId }),
    onSuccess: () => invalidateHomePlanQueries(queryClient, builderId),
  });
}

export function useUpdateHomePlans(builderId: string) {
  const queryClient = useQueryClient();
  return useMutation<BulkHomePlanMutationResult, never, HomePlanUpdate[]>({
    mutationKey: [...homePlanKeys.all(builderId), "bulk-update"],
    mutationFn: async (updates) => {
      const successfulHomePlanIds: string[] = [];
      const failures: BulkHomePlanMutationResult["failures"] = [];
      await Promise.all(
        updates.map(async ({ homePlanId, fields }) => {
          try {
            await updateHomePlan({ builderId, homePlanId, fields });
            successfulHomePlanIds.push(homePlanId);
          } catch (error) {
            failures.push({ homePlanId, error: error as FetchErrorResponse });
          }
        }),
      );
      return { successfulHomePlanIds, failures };
    },
    onSuccess: ({ successfulHomePlanIds }) => {
      if (successfulHomePlanIds.length > 0) {
        invalidateHomePlanQueries(queryClient, builderId);
      }
    },
  });
}

export function useDeleteHomePlans(builderId: string) {
  const queryClient = useQueryClient();
  return useMutation<BulkHomePlanMutationResult, never, string[]>({
    mutationKey: [...homePlanKeys.all(builderId), "bulk-delete"],
    mutationFn: async (homePlanIds) => {
      const successfulHomePlanIds: string[] = [];
      const failures: BulkHomePlanMutationResult["failures"] = [];
      await Promise.all(
        homePlanIds.map(async (homePlanId) => {
          try {
            await deleteHomePlan({ builderId, homePlanId });
            successfulHomePlanIds.push(homePlanId);
          } catch (error) {
            failures.push({ homePlanId, error: error as FetchErrorResponse });
          }
        }),
      );
      return { successfulHomePlanIds, failures };
    },
    onSuccess: ({ successfulHomePlanIds }) => {
      if (successfulHomePlanIds.length > 0) {
        invalidateHomePlanQueries(queryClient, builderId);
      }
    },
  });
}
